import { useCallback, useEffect, useMemo, useState } from 'react';
import Modal from 'react-responsive-modal';
import { ShareButton } from '../../../../components/ShareButtons';
import { Notify } from '../../../../components/Notify';
import styles from './LandingBusinessCard.module.css';
import { ModalPosterPrint } from '../../../../components/ModalPosterPrint';
import { useAuth } from '../../../../hooks/useAuth';
import { useLandingBusiness } from '../../../../hooks/useLandingBusiness';
import { useRating } from '../../../../hooks/useRating';
import { QrcodeWithPdf } from '../../../../components/QrcodeWithPdf';
import QRCode from 'qrcode.react';
import * as routes from '../../../../constants/Routes';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../../../../server/api';
import { ILandingBusiness } from '../../../../interfaces/ILandingBusiness';
import { Button } from '../../../../components/Button';

export function LandingBusinessCard({
  id,
  title,
  image,
  region,
  neighborhood,
  city,
  summary,
  shareUrl,
  autoIndicate,
}) {
  const { client, player } = useAuth();
  const { getLandingBusinessPage } = useLandingBusiness();
  const { getRating, rating } = useRating();
  const navigate = useNavigate();
  const [posterModalIsOpen, setPosterModalIsOpen] = useState(false);
  const [qrCodeModalIsOpen, setQrCodeModalIsOpen] = useState(false);
  const [share, setShare] = useState(false);
  const [cartazCover, setCartazCover] = useState<any>({});
  const [landingBusiness, setLandingBusiness] = useState({});
  const [fullLandingBusiness, setFullLandingBusiness] =
    useState<ILandingBusiness | null>(null);
  const [loadingPoster, setLoadingPoster] = useState(false);
  const [playerPhotoBase64, setPlayerPhotoBase64] = useState<string>('');

  useEffect(() => {
    setLandingBusiness({
      id: id,
      shareUrl: shareUrl,
      actionTitle: title,
      images: image,
      defaultImagePoster: image,
    });
  }, []);

  const minimalLandingBusiness = useMemo<ILandingBusiness>(
    () => ({
      id,
      active: true,
      clientId: player?.clientId || '',
      shareUrl,
      details: {
        name: title,
        summary,
        location: {
          city: city || '',
          state: region || '',
          neighborhood: neighborhood || '',
        },
        constructionStage: {},
        condominium: {},
        neighborhood: {} as any,
        paymentConditions: {},
      },
      medias: {
        images: {
          housingUnitImages: image ? [image] : [],
          commonAreasImages: [],
        },
      },
      creationDate: '',
    }),
    [id, shareUrl, title, summary, city, region, neighborhood, image, player]
  );

  const imageUrlToBase64 = async (url: any): Promise<any> => {
    return new Promise((resolve, reject) => {
      if (!url) {
        reject(new Error('URL is required'));
        return;
      }

      const img = new Image();

      // Permite imagens de origens diferentes (CORS)
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;

          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Failed to get canvas context'));
            return;
          }

          ctx.drawImage(img, 0, 0);

          try {
            const base64 = canvas.toDataURL('image/png');
            resolve(base64);
          } catch (canvasError) {
            // Se o canvas estiver "tainted" (bloqueado por CORS), retorna a URL original
            // Nota: O servidor precisa permitir CORS para que isso funcione
            // Configure CORS no bucket S3 ou use um proxy no backend
            console.warn(
              'Canvas blocked by CORS policy. The image server needs to allow CORS. Returning original URL:',
              canvasError
            );
            resolve(url);
          }
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = () => {
        // Se falhar com crossOrigin, tenta sem ele (pode funcionar se a imagem já estiver em cache)
        if (img.crossOrigin) {
          const imgRetry = new Image();
          imgRetry.onload = () => {
            try {
              const canvas = document.createElement('canvas');
              canvas.width = imgRetry.width;
              canvas.height = imgRetry.height;

              const ctx = canvas.getContext('2d');
              if (!ctx) {
                reject(new Error('Failed to get canvas context'));
                return;
              }

              ctx.drawImage(imgRetry, 0, 0);

              try {
                const base64 = canvas.toDataURL('image/png');
                resolve(base64);
              } catch (canvasError) {
                // Se ainda falhar, retorna a URL original
                console.warn(
                  'Canvas blocked, returning original URL:',
                  canvasError
                );
                resolve(url);
              }
            } catch (err) {
              reject(err);
            }
          };
          imgRetry.onerror = () => {
            // Se tudo falhar, retorna a URL original como fallback
            console.warn('Image loading failed, returning original URL');
            resolve(url);
          };
          imgRetry.src = url;
        } else {
          reject(new Error('Failed to load image'));
        }
      };

      img.src = url;
    });
  };

  const increaseShareChannelCount = async (channel: string) => {
    try {
      const parameters = {
        updateFunctionality: 'increaseShareChannelCount',
        userType: 'player',
        actionId: id,
        playerId: player.id,
        channel,
        clientId: player.clientId,
      };

      await api.post('/update', parameters);
    } catch (error) {
      console.log('error increaseShareChannelCount-->', error);
    }
  };

  const handleOpenPosterModal = useCallback(async () => {
    setPosterModalIsOpen(true);

    if (fullLandingBusiness) return;

    setLoadingPoster(true);
    getRating(player.id);
    try {
      const lb = await getLandingBusinessPage(player.id, id);
      const fetchedLb: ILandingBusiness = lb.landingBusiness;
      setFullLandingBusiness(fetchedLb);

      const housingImages = fetchedLb?.medias?.images?.housingUnitImages || [];
      const commonImages = fetchedLb?.medias?.images?.commonAreasImages || [];
      const allImages = [...housingImages, ...commonImages].slice(0, 4);

      const [logoImgBase64, qrCodeBase64, photoBase64, imagesBase64] =
        await Promise.all([
          imageUrlToBase64(client?.logoURL),
          imageUrlToBase64(
            `https://api.qrserver.com/v1/create-qr-code/?data=${fetchedLb.shareUrl || shareUrl}`
          ),
          player?.photoURL
            ? imageUrlToBase64(player.photoURL).catch(() => player.photoURL)
            : Promise.resolve(''),
          Promise.all(
            allImages.map((url) => imageUrlToBase64(url).catch(() => url))
          ),
        ]);

      setPlayerPhotoBase64(photoBase64);

      setCartazCover({
        imageUrl: allImages[0],
        imgBase64: imagesBase64[0] || '',
        imagesBase64,
        logoImgBase64,
        qrCodeBase64,
        playerPhotoBase64: photoBase64,
      });
    } catch (error) {
      console.error('Error loading poster data:', error);
    } finally {
      setLoadingPoster(false);
    }
  }, [fullLandingBusiness, id, player.id, client?.logoURL, shareUrl]);

  return (
    <div className="col-12 col-sm-12 col-md-6 col-lg-4" key={id} id={id}>
      <div className={styles.cardReveal}>
        <div className={styles.cardBanner} style={{ position: 'relative' }}>
          <img src={image} alt="imagem" />
          {(region || neighborhood) && (
            <div
              style={{
                position: 'absolute',
                left: '50%',
                bottom: 0,
                transform: 'translateX(-50%)',
                background: client.primaryColor,
                color: '#fff',
                borderTopLeftRadius: '12px',
                borderTopRightRadius: '12px',
                padding: '8px 14px',
                display: 'flex',
                alignItems: 'center',
                fontSize: '0.95rem',
                gap: '10px',
                zIndex: 1,
                whiteSpace: 'nowrap',
              }}
            >
              <span style={{ fontSize: 14 }}>{region || neighborhood}</span>
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: 14,
                }}
              >
                <i
                  className="material-icons"
                  style={{ fontSize: 16, verticalAlign: 'middle' }}
                >
                  location_on
                </i>
                {city}
              </span>
            </div>
          )}
        </div>
        <div className={`${styles.cardTitle} text-truncate p-4`}>
          {title && (
            <span
              style={{
                marginTop: '8px',
                marginBottom: '8px',
                fontWeight: 600,
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap', // Força o título em uma linha com "..."
              }}
            >
              {title}
            </span>
          )}
          <span
            style={{
              fontSize: '0.85em',
              fontWeight: '500',
              display: 'block',
              overflow: 'hidden',
              wordWrap: 'break-word', // Quebra palavras longas
              whiteSpace: 'normal', // Permite quebra de linha
              wordBreak: 'break-word', // Quebra palavras que excedem a largura
            }}
          >
            {summary}
          </span>
        </div>
        {client?.clientUrl !== 'motamachado' &&
          client?.settings?.landingBusiness?.showShareButton !== false && (
            <div
              className={styles.clipboard}
              style={{
                maxWidth: '90%',
                marginLeft: 'auto',
                marginRight: 'auto',
                marginBottom: '15px',
              }}
            >
              <input
                className="px-4"
                placeholder="Link encurtado"
                value={shareUrl}
                disabled
                type="text"
              />
              <button
                onClick={async () => {
                  try {
                    navigator.clipboard.writeText(shareUrl);
                    Notify('Texto copiado com sucesso', 'success');
                    await increaseShareChannelCount('copiedlink');
                  } catch (error) {
                    Notify('Erro ao copiar texto', 'error');
                  }
                }}
                className="p-4"
              >
                <i className="material-icons-outlined">content_copy</i>
              </button>
            </div>
          )}
        <div className={`${styles.cardAction} mt-4`}>
          <button
            onClick={() => {
              navigate(`${routes.LANDING_BUSSINESS}/${id}`);
            }}
          >
            <i className="material-icons-outlined mx-2">add</i>
            <span>Mais detalhes</span>
          </button>
          {client?.clientUrl !== 'motamachado' &&
            client?.settings?.landingBusiness?.showShareButton !== false && (
              <button
                className={styles.cta}
                onClick={() => {
                  setShare(true);
                }}
              >
                <i className="material-icons-outlined mx-2">share</i>
                <span>Divulgar agora</span>
              </button>
            )}
        </div>
        {share && (
          <div className={styles.revealWrapper}>
            <div
              className={`${styles.reveal} ${share ? styles.open : ''}`}
              style={
                share && autoIndicate
                  ? { transform: 'translateY(-440px)' }
                  : undefined
              }
            >
              <div className={`${styles.content} p-4`}>
                <div className={styles.topReveal}>
                  <span style={{ color: client.primaryColor }}>Divulgar</span>
                  <button onClick={() => setShare(false)}>
                    <i className="material-icons-outlined">close</i>
                  </button>
                </div>
                <div className="container">
                  <span>Divulgue rapidamente via</span>
                  <div className="my-3">
                    <ShareButton action={landingBusiness} />
                  </div>
                  <span>Ou copie o link</span>
                  <div className="mt-3">
                    <div className={styles.clipboard}>
                      <input
                        className="px-4"
                        placeholder="Link encurtado"
                        value={shareUrl}
                        disabled
                        type="text"
                      />
                      <button
                        onClick={async () => {
                          try {
                            navigator.clipboard.writeText(shareUrl);
                            Notify('Texto copiado com sucesso', 'success');
                            await increaseShareChannelCount('copiedlink');
                          } catch (error) {
                            Notify('Erro ao copiar texto', 'error');
                          }
                        }}
                        className="p-4"
                      >
                        <i className="material-icons-outlined">content_copy</i>
                      </button>
                    </div>
                  </div>
                </div>
                <div className="container my-3" style={{ display: 'flex' }}>
                  <button
                    onClick={() => {
                      setQrCodeModalIsOpen(true);
                    }}
                    style={{
                      display: 'block',
                      background: 'none',
                      border: 'none',
                    }}
                  >
                    <span style={{ fontWeight: '700' }}>Gerar QR Code</span>
                    <i className="material-icons-outlined mx-2">
                      qr_code_scanner
                    </i>
                  </button>
                  <button
                    onClick={handleOpenPosterModal}
                    style={{
                      display: 'block',
                      background: 'none',
                      border: 'none',
                    }}
                  >
                    <span style={{ fontWeight: '700' }}>Gerar Cartaz</span>
                    <i className="material-icons-outlined mx-2">web</i>
                  </button>
                </div>
                {autoIndicate && (
                  <div className="mt-3">
                    <Link to={`${routes.LANDING_BUSSINESS}/${id}?auto=true`}>
                      <div className="d-grid">
                        <Button
                          text={
                            player && player.regional === 'sim'
                              ? 'Registrar Lead'
                              : 'Indicar Agora'
                          }
                          color="primary"
                          icon="new_releases"
                        />
                      </div>
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        <ModalPosterPrint
          modalIsOpen={posterModalIsOpen}
          closeModal={() => setPosterModalIsOpen(false)}
          clientPrimaryColor={client && client.primaryColor}
          playerName={player && player.name}
          action={landingBusiness}
          shareUrl={shareUrl}
          actionImagesBase64={cartazCover.imgBase64}
          cartazCover={cartazCover}
          isLandingBusiness={true}
          landingBusiness={fullLandingBusiness || minimalLandingBusiness}
          playerPhone={player?.phoneNumber || ''}
          playerPhoto={playerPhotoBase64 || player?.photoURL}
          playerRating={rating?.averageRating}
          imagesBase64={cartazCover?.imagesBase64}
          showPlayerContact={player?.regional === 'sim'}
          loadingPoster={loadingPoster}
        />
        <Modal
          open={qrCodeModalIsOpen}
          onClose={() => setQrCodeModalIsOpen(false)}
          center
        >
          <div style={{ margin: '15px' }}>
            <div style={{ textAlign: 'center' }}>
              <h2>Qr code</h2>
            </div>
            {shareUrl && (
              <QRCode
                style={{ display: 'none' }}
                id="canvas-qrcode-share-url"
                value={shareUrl}
                size={600}
                className="text-center qrcode"
              />
            )}
            <QrcodeWithPdf valueQrcode={shareUrl ? shareUrl : ''} />
          </div>
        </Modal>
      </div>
    </div>
  );
}
